import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Combobox,
  Field,
  Input,
  PageHeader,
  Select,
  SkeletonRows,
  Textarea,
  Toolbar,
  cn,
  formatDate,
  formatMoney,
  formatQty,
  humanizeStatus,
  isDecimal,
  cmpDecimal,
  mulDecimal,
  roundDecimalString,
  subDecimal,
  sumDecimal,
} from '@biovet/ui';
import { ApiError, useResource } from '../lib/api.js';
import * as brokerage from '../lib/brokerage.js';
import type { AvailabilityRow, BrokerageRefs, DealPayload } from '../lib/brokerage.js';
import { CurrencyRateFields, Th } from './parts.js';
import { allCurrencies, baseCurrency, rateFor } from '../lib/reference-data.js';
import { useAutoRows } from './document.js';

/**
 * Deal entry — the brokerage document, read and written.
 *
 * **The golden rule is drawn, not just obeyed.** The goods block and the
 * settlement block are labelled memorandum, and the only figures with a GL
 * effect are in the commission panel. `0015_invariants.sql` refuses a voucher
 * whose `source_table` is `deals`, so nothing on this screen can post one even
 * by accident. The single place Part 1 meets the ledger is **Raise commission
 * invoice**, which posts one COM voucher — Dr commission receivable, Cr
 * commission income — against the accounts the chart carries the system keys
 * `AR_COMMISSION` and `COMMISSION_INCOME` on.
 *
 * **The deal is the workflow, and the workflow is the screen.** Enter → confirm
 * → ship → deliver → acknowledge what the buyer paid the supplier → invoice the
 * commission that earned → receive it. Each step is a button that exists only
 * when the step is actually available, because a button that explains why it did
 * nothing is worse than no button.
 *
 * Amounts are decimal strings from the API to the input and back. The previews
 * below use `mulDecimal`, which reproduces PostgreSQL's `numeric` multiplication
 * exactly — so the figure on screen before saving is the figure the database
 * computes after.
 */

// ── Shapes ───────────────────────────────────────────────────────────────────

interface DealHeader {
  dealNo: string;
  dealDate: string;
  kind: string;
  status: string;
  partyA: string;
  partyB: string;
  partyACode: string;
  partyBCode: string;
  currency: string;
  fxRate: string;
  dealValue: string;
  dealValueBase: string;
  branchCode: string | null;
  commissionBasis: string;
  commissionRate: string | null;
  commissionAmount: string;
  commissionCurrency: string;
  commissionFxRate: string;
  commissionBase: string;
  accrualPolicy: string;
  payableBase: string;
  confirmedBase: string;
  balanceBase: string;
  invoicedBase: string;
  receivedBase: string;
  confirmedAt: string | null;
  deliveredAt: string | null;
  settledAt: string | null;
  closedAt: string | null;
  narration: string | null;
  commissionInvoiceNo: string | null;
}

interface DealLine {
  lineNo: number;
  product: string;
  quantity: string;
  uom: string;
  rate: string;
  commissionRate: string | null;
  currency: string;
  value: string;
}

interface DealSplit {
  lineNo: number;
  beneficiary: string | null;
  kind: string;
  subAgentCode: string | null;
  salespersonCode: string | null;
  sharePct: string | null;
  fixedAmountBase: string | null;
  amountBase: string;
}

interface DealSettlementRow {
  seqNo: number;
  settlementDate: string;
  currency: string;
  amount: string;
  amountBase: string;
  paymentMethod: string | null;
  proofReference: string | null;
  confirmed: boolean;
  commissionReleasedBase: string;
}

interface DealDetail {
  header: DealHeader;
  lines: DealLine[];
  splits: DealSplit[];
  settlements: DealSettlementRow[];
}

interface DealListRow {
  dealNo: string;
  partyA: string;
  partyB: string;
  status: string;
  dealDate: string;
}

/** A goods line while it is being typed: every field is a string, including the
 *  numbers, because a half-typed `12.` is not a number yet and must not become
 *  `NaN` on its way through the form. */
interface LineDraft {
  productName: string;
  quantity: string;
  uomCode: string;
  unitRate: string;
}

interface SplitDraft {
  /** `party:CODE` or `sales:CODE` — one control, one of two tables. */
  beneficiary: string;
  /** A share of the commission. Exclusive with `fixedAmountBase`. */
  sharePct: string;
  /** A flat amount in PKR instead of a share. Exclusive with `sharePct`. */
  fixedAmountBase: string;
}

const EMPTY_SPLIT: SplitDraft = { beneficiary: '', sharePct: '', fixedAmountBase: '' };

/**
 * A deal while it is being entered.
 *
 * Deliberately short. Everything a broker actually knows at the moment a deal is
 * struck is here — the two parties, the goods, the money and the commission —
 * and everything they learn *later* has been taken out: contract references,
 * delivery terms, and the whole shipment block, which is filled in on the import
 * tracker as the vessel is booked and the B/L issued rather than guessed at
 * entry.
 *
 * `branchCode` survives as a field but not as a control: branch is tenancy, it
 * is already chosen once on the top bar, and asking again on every deal invited
 * two answers to the same question.
 */
interface DealDraft {
  dealNo: string | null;
  dealDate: string;
  kind: 'LOCAL' | 'IMPORT';
  partyACode: string;
  partyBCode: string;
  currency: string;
  fxRate: string;
  commissionBasis: string;
  commissionRate: string;
  commissionAmount: string;
  commissionCurrency: string;
  commissionFxRate: string;
  accrualPolicy: string;
  /** From the top bar, not from a control on this form. */
  branchCode: string;
  narration: string;
  lines: LineDraft[];
  splits: SplitDraft[];
}

const EMPTY_LINE: LineDraft = {
  productName: '',
  quantity: '',
  uomCode: '',
  unitRate: '',
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * A new deal, opened on the register's own numbers.
 *
 * **The rate comes from the currency register, not from the literal 1.** A deal
 * opens in USD, and a USD deal carrying a rate of 1 converts a hundred thousand
 * dollars into a hundred thousand rupees — a memorandum figure that looks
 * entered and is wrong by the whole exchange rate. `rateFor()` reads the latest
 * SPOT that `GET /api/context` returned, which is the same number the top bar
 * shows and the same one `Update` there writes; a currency `fx_rates` has never
 * carried leaves the field **blank** rather than at par, because par is the one
 * wrong answer that looks like it worked. Same rule as `CurrencyRateFields`,
 * which is what re-applies it when the currency is changed on the form.
 *
 * The commission currency is the book's own — reading it rather than writing
 * `'PKR'`, so a book kept in another base does not open every deal on a foreign
 * commission whose rate nobody entered.
 */
function blankDraft(branchCode: string): DealDraft {
  const dealCurrency = allCurrencies().some((c) => c.code === 'USD') ? 'USD' : baseCurrency();
  return {
    dealNo: null,
    dealDate: today(),
    kind: 'IMPORT',
    partyACode: '',
    partyBCode: '',
    currency: dealCurrency,
    fxRate: rateFor(dealCurrency) ?? '',
    commissionBasis: 'PCT_OF_DEAL_VALUE',
    commissionRate: '',
    commissionAmount: '',
    commissionCurrency: baseCurrency(),
    commissionFxRate: '1',
    accrualPolicy: 'ON_FULL_SETTLEMENT',
    branchCode,
    narration: '',
    lines: [{ ...EMPTY_LINE }],
    splits: [{ ...EMPTY_SPLIT }],
  };
}

function draftFrom(detail: DealDetail): DealDraft {
  const h = detail.header;
  return {
    dealNo: h.dealNo,
    dealDate: h.dealDate?.slice(0, 10) ?? today(),
    kind: h.kind === 'IMPORT' ? 'IMPORT' : 'LOCAL',
    partyACode: h.partyACode,
    partyBCode: h.partyBCode,
    currency: h.currency,
    fxRate: h.fxRate,
    commissionBasis: h.commissionBasis,
    commissionRate: h.commissionRate ?? '',
    commissionAmount: h.commissionAmount ?? '',
    commissionCurrency: h.commissionCurrency,
    commissionFxRate: h.commissionFxRate ?? '1',
    accrualPolicy: h.accrualPolicy,
    branchCode: h.branchCode ?? '',
    narration: h.narration ?? '',
    lines: detail.lines.map((l) => ({
      productName: l.product,
      quantity: l.quantity,
      uomCode: l.uom,
      unitRate: l.rate,
    })),
    splits: detail.splits.map((s) => ({
      beneficiary: s.subAgentCode
        ? `party:${s.subAgentCode}`
        : s.salespersonCode
          ? `sales:${s.salespersonCode}`
          : '',
      sharePct: s.sharePct ?? '',
      fixedAmountBase: s.fixedAmountBase ?? '',
    })),
  };
}

/**
 * The draft as the API wants it.
 *
 * The shipment fields are absent rather than sent empty, and that is the point:
 * `saveDeal` replaces a draft wholesale, so a form that posted `vessel: null`
 * would wipe a vessel the import tracker had recorded. Leaving a key out leaves
 * the column alone.
 */
function toPayload(draft: DealDraft): DealPayload {
  return {
    dealNo: draft.dealNo,
    dealDate: draft.dealDate,
    kind: draft.kind,
    partyACode: draft.partyACode,
    partyBCode: draft.partyBCode,
    currency: draft.currency,
    fxRate: draft.currency === baseCurrency() ? '1' : draft.fxRate,
    commissionBasis: draft.commissionBasis,
    commissionRate: draft.commissionBasis === 'FLAT_AMOUNT' ? null : draft.commissionRate || null,
    commissionAmount: draft.commissionBasis === 'FLAT_AMOUNT' ? draft.commissionAmount : null,
    commissionCurrency: draft.commissionCurrency,
    commissionFxRate: draft.commissionCurrency === baseCurrency() ? '1' : draft.commissionFxRate,
    accrualPolicy: draft.accrualPolicy,
    branchCode: draft.branchCode || null,
    narration: draft.narration || null,
    lines: draft.lines
      .filter((l) => l.productName.trim() !== '')
      .map((l) => ({
        productName: l.productName.trim(),
        quantity: l.quantity,
        uomCode: l.uomCode,
        unitRate: l.unitRate,
      })),
    splits: draft.splits
      .filter((s) => s.beneficiary !== '')
      .map((s) => {
        const [table, code] = s.beneficiary.split(':');
        return {
          subAgentCode: table === 'party' ? code : null,
          salespersonCode: table === 'sales' ? code : null,
          sharePct: s.sharePct || null,
          fixedAmountBase: s.fixedAmountBase || null,
        };
      }),
  };
}

/** What the form can say before the API does. Nothing here duplicates a rule the
 *  database owns — these are the four the user can fix without a round trip. */
function complaints(draft: DealDraft): string[] {
  const issues: string[] = [];
  if (!draft.partyACode || !draft.partyBCode) issues.push('Both parties are required.');
  if (draft.partyACode && draft.partyACode === draft.partyBCode) {
    issues.push('Party A and Party B must be different.');
  }
  const filled = draft.lines.filter((l) => l.productName.trim() !== '');
  if (filled.length === 0) issues.push('At least one goods line is required.');
  for (const [i, l] of filled.entries()) {
    if (!isDecimal(l.quantity) || Number(l.quantity) <= 0) {
      issues.push(`Line ${i + 1}: quantity must be a positive number.`);
    }
    if (!isDecimal(l.unitRate)) issues.push(`Line ${i + 1}: rate must be a number.`);
    if (!l.uomCode) issues.push(`Line ${i + 1}: pick a unit.`);
  }
  if (draft.commissionBasis === 'FLAT_AMOUNT') {
    if (!isDecimal(draft.commissionAmount) || Number(draft.commissionAmount) <= 0) {
      issues.push('A flat commission needs an amount.');
    }
  } else if (!isDecimal(draft.commissionRate)) {
    issues.push(
      draft.commissionBasis === 'PCT_OF_DEAL_VALUE'
        ? 'Commission percentage is required.'
        : 'Commission per unit is required.',
    );
  }
  if (
    draft.currency !== baseCurrency() &&
    (!isDecimal(draft.fxRate) || Number(draft.fxRate) <= 0)
  ) {
    issues.push(`A ${draft.currency} deal needs a positive rate.`);
  }
  // The picker already withholds a beneficiary another row has taken; this
  // catches a deal saved before it did, re-opened for editing.
  const named = draft.splits.map((s) => s.beneficiary).filter((b) => b !== '');
  if (new Set(named).size !== named.length) {
    issues.push('One beneficiary is on two commission splits — combine them into one share.');
  }
  return issues;
}

/** The same arithmetic the database will do, so the preview cannot disagree. */
function preview(draft: DealDraft): { value: string; valueBase: string; commission: string } {
  const filled = draft.lines.filter(
    (l) => isDecimal(l.quantity) && isDecimal(l.unitRate) && l.productName.trim() !== '',
  );
  const value = roundDecimalString(
    sumDecimal(filled.map((l) => mulDecimal(l.quantity, l.unitRate))),
    4,
  );
  const rate =
    draft.currency === baseCurrency() ? '1' : isDecimal(draft.fxRate) ? draft.fxRate : '1';
  const valueBase = roundDecimalString(mulDecimal(value, rate), 4);

  let commission = '0';
  if (draft.commissionBasis === 'PCT_OF_DEAL_VALUE' && isDecimal(draft.commissionRate)) {
    const gross = roundDecimalString(
      mulDecimal(mulDecimal(value, draft.commissionRate), '0.01'),
      4,
    );
    commission = roundDecimalString(mulDecimal(gross, rate), 4);
  } else if (draft.commissionBasis === 'PER_UNIT' && isDecimal(draft.commissionRate)) {
    const qty = sumDecimal(filled.map((l) => l.quantity));
    const gross = roundDecimalString(mulDecimal(qty, draft.commissionRate), 4);
    const cRate =
      draft.commissionCurrency === baseCurrency()
        ? '1'
        : isDecimal(draft.commissionFxRate)
          ? draft.commissionFxRate
          : '1';
    commission = roundDecimalString(mulDecimal(gross, cRate), 4);
  } else if (draft.commissionBasis === 'FLAT_AMOUNT' && isDecimal(draft.commissionAmount)) {
    const cRate =
      draft.commissionCurrency === baseCurrency()
        ? '1'
        : isDecimal(draft.commissionFxRate)
          ? draft.commissionFxRate
          : '1';
    commission = roundDecimalString(mulDecimal(draft.commissionAmount, cRate), 4);
  }

  return { value, valueBase, commission };
}

// ── Screen ───────────────────────────────────────────────────────────────────

export function DealEntryScreen({
  branchCode = '',
  dealNo: requested = null,
  onDealChange,
}: {
  branchCode?: string;
  /** Which deal to open. Set by the register when a row is clicked. */
  dealNo?: string | null;
  onDealChange?: (dealNo: string | null) => void;
}) {
  const listRes = useResource<{ deals: DealListRow[] }>('/deals');
  const refsRes = useResource<BrokerageRefs>('/brokerage-refs');
  const list = useMemo(() => listRes.data?.deals ?? [], [listRes.data]);

  const [draft, setDraft] = useState<DealDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  /**
   * Which deal is open.
   *
   * The choice lives in `App` so a click in the deal register lands here on the
   * row that was clicked. With nothing chosen it falls back to the most recent
   * deal, which is what the register sorts to the top.
   */
  const setChosen = onDealChange ?? (() => {});
  const dealNo = requested ?? list[0]?.dealNo ?? null;
  const res = useResource<DealDetail>(dealNo && !draft ? '/deal' : null, {
    no: dealNo ?? undefined,
  });

  const refs = refsRes.data;

  const reloadAll = () => {
    listRes.reload();
    res.reload();
  };

  const run = async <T extends { message: string }>(work: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setProblem(null);
    setNotice(null);
    try {
      const result = await work();
      setNotice(result.message);
      reloadAll();
      return result;
    } catch (cause) {
      setProblem(cause instanceof ApiError ? cause.full : String(cause));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const startNew = () => {
    setProblem(null);
    setNotice(null);
    setDraft(blankDraft(branchCode));
  };

  const startEdit = () => {
    if (!res.data) return;
    setProblem(null);
    setNotice(null);
    setDraft(draftFrom(res.data));
  };

  const saveDraft = async () => {
    if (!draft) return;
    const issues = complaints(draft);
    if (issues.length > 0) {
      setProblem(issues.join(' '));
      return;
    }
    const saved = await run(() => brokerage.saveDeal(toPayload(draft)));
    if (saved) {
      setChosen(saved.dealNo);
      setDraft(null);
    }
  };

  if (listRes.error || refsRes.error) {
    return (
      <>
        <PageHeader breadcrumb="Brokerage / Deal Entry" title="Deal entry" />
        <Callout tone="danger" title="Brokerage could not be loaded">
          {listRes.error ?? refsRes.error}
        </Callout>
      </>
    );
  }

  if (draft && refs) {
    return (
      <DealForm
        draft={draft}
        refs={refs}
        busy={busy}
        notice={notice}
        problem={problem}
        onChange={setDraft}
        onCancel={() => {
          setDraft(null);
          setProblem(null);
        }}
        onSave={saveDraft}
      />
    );
  }

  if (!res.data || !refs) {
    return (
      <>
        <PageHeader
          breadcrumb="Brokerage / Deal Entry"
          title="Deal entry"
          actions={
            <Button size="sm" variant="primary" onClick={startNew} disabled={!refs}>
              New deal
            </Button>
          }
        />
        {list.length === 0 && !listRes.loading ? (
          <Callout tone="info" title="No deals yet">
            Enter the first one — Party A sells direct to Party B, we connect them, own nothing, and
            invoice commission on Party A.
          </Callout>
        ) : (
          <Card flush>
            <CardBody>
              <SkeletonRows rows={8} cols={4} />
            </CardBody>
          </Card>
        )}
      </>
    );
  }

  return (
    <DealView
      detail={res.data}
      list={list}
      refs={refs}
      busy={busy}
      notice={notice}
      problem={problem}
      error={res.error}
      onChoose={setChosen}
      onNew={startNew}
      onEdit={startEdit}
      run={run}
      onDeleted={() => setChosen(null)}
    />
  );
}

// ── View ─────────────────────────────────────────────────────────────────────

function DealView({
  detail,
  list,
  refs,
  busy,
  notice,
  problem,
  error,
  onChoose,
  onNew,
  onEdit,
  run,
  onDeleted,
}: {
  detail: DealDetail;
  list: DealListRow[];
  refs: BrokerageRefs;
  busy: boolean;
  notice: string | null;
  problem: string | null;
  error: string | undefined;
  onChoose: (dealNo: string) => void;
  onNew: () => void;
  onEdit: () => void;
  run: <T extends { message: string }>(work: () => Promise<T>) => Promise<T | null>;
  onDeleted: () => void;
}) {
  const { header, lines, splits, settlements } = detail;
  const gross = header.commissionBase;
  const shared = sumDecimal(splits.map((s) => s.amountBase));
  const [panel, setPanel] = useState<'none' | 'settle' | 'invoice' | 'receipt' | 'cancel'>('none');

  useEffect(() => setPanel('none'), [header.dealNo]);

  const rank = DEAL_RANK[header.status] ?? 0;
  const settled = Number(header.balanceBase) === 0 && Number(header.payableBase) > 0;

  /**
   * What is earned right now.
   *
   * The API computes this again in `numeric` before it will invoice anything —
   * this copy exists so the button can name the figure instead of the user
   * pressing it to find out. The pro-rata ratio goes through a JavaScript number
   * because a fraction has no exact decimal form; the result is rounded to four
   * places and is a label, never an amount that is posted.
   */
  const earned =
    header.accrualPolicy === 'PRO_RATA'
      ? Number(header.payableBase) > 0
        ? roundDecimalString(
            mulDecimal(
              header.commissionBase,
              (Number(header.confirmedBase) / Number(header.payableBase)).toFixed(8),
            ),
            4,
          )
        : '0'
      : settled
        ? header.commissionBase
        : '0';
  const invoiceable = subDecimal(earned, header.invoicedBase);
  const canInvoice = cmpDecimal(invoiceable, '0') > 0;

  const nextSteps = MANUAL_STEPS.filter((s) => (DEAL_RANK[s.status] ?? 0) > rank).slice(0, 1);

  return (
    <>
      <PageHeader
        breadcrumb="Brokerage / Deal Entry"
        title={`Deal ${header.dealNo}`}
        description="We introduce the seller to the buyer and earn commission on the deal."
        meta={
          <>
            <Badge status={header.status}>{humanizeStatus(header.status)}</Badge>
            <span className="text-[12.5px] text-[var(--ink-2)]">
              {header.partyA} → {header.partyB}
            </span>
            <span className="text-[12.5px] text-[var(--ink-3)]">
              {humanizeStatus(header.kind)} · {formatDate(header.dealDate)}
            </span>
            <span className="text-[12.5px] text-[var(--ink-3)]">
              {humanizeStatus(header.accrualPolicy)}
            </span>
          </>
        }
        actions={
          <>
            <Select
              size="sm"
              aria-label="Deal"
              value={header.dealNo}
              onChange={(e) => onChoose(e.target.value)}
            >
              {list.map((d) => (
                <option key={d.dealNo} value={d.dealNo}>
                  {d.dealNo} · {d.partyA}
                </option>
              ))}
            </Select>
            <Button size="sm" onClick={onNew}>
              New deal
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-2.5 pb-3.5" data-noprint="1">
        {error && (
          <Callout tone="danger" title="This deal could not be reloaded">
            {error}
          </Callout>
        )}
        {problem && (
          <Callout tone="danger" title="That step was refused">
            {problem}
          </Callout>
        )}
        {notice && (
          <Callout tone="success" title="Done">
            {notice}
          </Callout>
        )}
      </div>

      <Callout tone="info" title="No GL entry for the goods">
        The deal value of {formatMoney(header.dealValueBase, { decimals: 0, showCurrency: true })}{' '}
        and the buyer&rsquo;s payments to the supplier are memorandum only. The database refuses a
        voucher sourced from <code className="bb-mono">deals</code>; commission is the single
        financial transaction in this line
        {header.commissionInvoiceNo ? `, invoiced as ${header.commissionInvoiceNo}` : ''}.
      </Callout>

      {/*
        Where the deal is, and the one thing to do about it — in one card.

        These were two: a six-stage vertical timeline down the right column and a
        row of buttons up here, so reading the state and acting on it meant
        looking in two places. The stepper says where you are; the buttons under
        it appear only while the step they perform is available.
      */}
      <Card flush data-noprint="1" className="mt-3.5">
        <div className="bb-card-head">
          <Stepper steps={pipeline(header)} />
        </div>
        <CardBody className="flex flex-wrap items-center gap-2">
          {header.status === 'DRAFT' && (
            <>
              <Button size="sm" onClick={onEdit} disabled={busy}>
                Edit deal
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={async () => {
                  const ok = await run(() => brokerage.deleteDeal(header.dealNo));
                  if (ok) onDeleted();
                }}
              >
                Delete draft
              </Button>
            </>
          )}

          {nextSteps.map((step) => (
            <Button
              key={step.status}
              size="sm"
              variant="primary"
              loading={busy}
              onClick={() => run(() => brokerage.advanceDeal(header.dealNo, step.status))}
            >
              {step.label}
            </Button>
          ))}

          {rank >= 1 && rank < 7 && (
            <Button size="sm" onClick={() => setPanel(panel === 'settle' ? 'none' : 'settle')}>
              Record buyer settlement
            </Button>
          )}

          {canInvoice && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => setPanel(panel === 'invoice' ? 'none' : 'invoice')}
            >
              Raise commission invoice · {formatMoney(invoiceable, { decimals: 0 })}
            </Button>
          )}

          {header.commissionInvoiceNo &&
            Number(header.receivedBase) < Number(header.invoicedBase) && (
              <Button
                size="sm"
                variant="primary"
                onClick={() => setPanel(panel === 'receipt' ? 'none' : 'receipt')}
              >
                Record commission receipt
              </Button>
            )}

          {/*
            Cancel is offered only while it still does something.

            Everything cancelling does is forward-looking — it stops the deal
            moving and stops commission being raised on it — so once a COM
            voucher exists both have already happened and pressing it changes a
            status and nothing else. The panel used to say as much, in a warning
            explaining that the invoice is "not withdrawn by this", which is a
            careful description of a button with no effect. `advanceDeal` refuses
            it now; this is the same rule, drawn rather than explained.
          */}
          {!header.commissionInvoiceNo &&
            header.status !== 'CANCELLED' &&
            header.status !== 'CLOSED' && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setPanel(panel === 'cancel' ? 'none' : 'cancel')}
              >
                Cancel deal
              </Button>
            )}

          {header.commissionInvoiceNo && header.status !== 'CANCELLED' && (
            <span className="text-[12px] text-[var(--ink-3)]">
              Commission is invoiced as{' '}
              <span className="bb-mono">{header.commissionInvoiceNo}</span>, so this deal can no
              longer be cancelled — reverse that voucher in the general ledger instead.
            </span>
          )}

          {!canInvoice && header.status !== 'DRAFT' && !header.commissionInvoiceNo && (
            <span className="text-[12px] text-[var(--ink-3)]">
              {header.accrualPolicy === 'PRO_RATA'
                ? `Nothing earned yet — the buyer has confirmed ${formatMoney(header.confirmedBase, { decimals: 0 })} of ${formatMoney(header.payableBase, { decimals: 0 })}.`
                : `This deal accrues on full settlement and ${formatMoney(header.balanceBase, { decimals: 0 })} is still owed to Party A.`}
            </span>
          )}
        </CardBody>

        {panel === 'cancel' && (
          <CancelPanel header={header} busy={busy} onClose={() => setPanel('none')} run={run} />
        )}
        {panel === 'settle' && (
          <SettlementPanel
            header={header}
            refs={refs}
            busy={busy}
            onClose={() => setPanel('none')}
            run={run}
          />
        )}
        {panel === 'invoice' && (
          <InvoicePanel
            header={header}
            splits={splits}
            invoiceable={invoiceable}
            busy={busy}
            onClose={() => setPanel('none')}
            run={run}
          />
        )}
        {panel === 'receipt' && header.commissionInvoiceNo && (
          <ReceiptPanel
            invoiceNo={header.commissionInvoiceNo}
            currency={header.commissionCurrency}
            estimatedRate={header.commissionFxRate}
            busy={busy}
            onClose={() => setPanel('none')}
            run={run}
          />
        )}
      </Card>

      <div className="grid gap-3.5 pt-3.5 xl:grid-cols-[1.6fr_1fr]">
        <div className="flex flex-col gap-3.5">
          <Card flush>
            <CardHeader
              title="Goods"
              subtitle="Memorandum · statistical data for reporting"
              actions={
                <span className="bb-num text-[11.5px] text-[var(--ink-3)]">
                  {header.currency} @ {header.fxRate}
                </span>
              }
            />
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[var(--surface-2)]">
                    <Th>Product</Th>
                    <Th align="right" width="130px">
                      Quantity
                    </Th>
                    <Th align="right" width="150px">
                      Rate
                    </Th>
                    <Th align="right" width="170px">
                      Value
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.lineNo} className="bb-row-h border-b border-[var(--line-2)]">
                      <td className="px-[var(--pad)] text-[13px] font-medium">{l.product}</td>
                      <td className="bb-num px-2.5 text-right text-[13px] tabular-nums">
                        {formatQty(l.quantity, 0)} {l.uom}
                      </td>
                      <td className="bb-num px-2.5 text-right text-[12.5px] tabular-nums">
                        {l.currency} {formatMoney(l.rate)}
                      </td>
                      <td className="bb-num px-[var(--pad)] text-right text-[13px] font-medium tabular-nums">
                        {l.currency} {formatMoney(l.value, { decimals: 0 })}
                      </td>
                    </tr>
                  ))}
                  <tr className="bb-row-h border-b border-[var(--line-2)] bg-[var(--surface-2)]">
                    <td className="px-[var(--pad)] text-[13px] font-semibold">
                      PKR equivalent @ {header.fxRate} · memorandum only, no GL entry
                    </td>
                    <td />
                    <td />
                    <td className="bb-num px-[var(--pad)] text-right text-[13px] font-semibold tabular-nums">
                      {formatMoney(header.dealValueBase, { decimals: 0 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          <Card flush>
            <CardHeader
              title="Settlement — Party B to Party A"
              subtitle="Acknowledgements only. None of this money passes through our books."
            />
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[var(--surface-2)]">
                    <Th width="60px">#</Th>
                    <Th width="130px">Date</Th>
                    <Th>Proof</Th>
                    <Th align="right" width="170px">
                      Amount PKR
                    </Th>
                    <Th align="right" width="180px">
                      Commission released
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.length === 0 && (
                    <tr className="bb-row-h border-b border-[var(--line-2)]">
                      <td colSpan={5} className="px-[var(--pad)] text-[12.5px] text-[var(--ink-3)]">
                        The buyer has not paid the supplier yet.
                      </td>
                    </tr>
                  )}
                  {settlements.map((s) => (
                    <tr key={s.seqNo} className="bb-row-h border-b border-[var(--line-2)]">
                      <td className="bb-num px-[var(--pad)] text-[12px] text-[var(--ink-3)]">
                        {s.seqNo}
                      </td>
                      <td className="bb-num px-2.5 text-[12.5px]">
                        {formatDate(s.settlementDate)}
                      </td>
                      <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">
                        {[s.paymentMethod, s.proofReference].filter(Boolean).join(' · ') || '—'}
                        {!s.confirmed && (
                          <span className="pl-2 text-[var(--warn)]">unconfirmed</span>
                        )}
                      </td>
                      <td className="bb-num px-2.5 text-right text-[13px] tabular-nums">
                        {formatMoney(s.amountBase, { decimals: 0 })}
                      </td>
                      <td className="bb-num px-[var(--pad)] text-right text-[12.5px] tabular-nums text-[var(--credit)]">
                        {formatMoney(s.commissionReleasedBase, { dashOnZero: true })}
                      </td>
                    </tr>
                  ))}
                  <tr className="bb-row-h bg-[var(--surface-2)]">
                    <td />
                    <td />
                    <td className="px-2.5 text-[12.5px] font-semibold">
                      Balance still owed to Party A
                    </td>
                    <td className="bb-num px-2.5 text-right text-[13px] font-semibold tabular-nums">
                      {formatMoney(header.balanceBase, { decimals: 0, dashOnZero: true })}
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <div className="flex flex-col gap-3.5">
          <Card flush>
            <CardHeader title="Commission" subtitle="The only GL event in this deal" />
            <CardBody className="flex flex-col gap-2.5">
              <Row
                label="Basis"
                value={describeBasis(header.commissionBasis, header.commissionRate)}
              />
              <Row label="Commission currency" value={header.commissionCurrency} />
              <Row label="Recognition" value={humanizeStatus(header.accrualPolicy)} />
              <Row label="Gross commission" value={formatMoney(gross)} strong />
              {splits.map((s) => (
                <Row
                  key={s.lineNo}
                  label={`${s.kind}${s.beneficiary ? ` · ${s.beneficiary}` : ''}${
                    s.sharePct ? ` (${Number(s.sharePct)}%)` : ''
                  }`}
                  value={`(${formatMoney(s.amountBase)})`}
                />
              ))}
              <Row label="Net to company" value={formatMoney(subDecimal(gross, shared))} strong />
              <Row label="Earned to date" value={formatMoney(earned, { dashOnZero: true })} />
              <Row
                label="Invoiced"
                value={formatMoney(header.invoicedBase, { dashOnZero: true })}
              />
              <Row
                label="Received"
                value={formatMoney(header.receivedBase, { dashOnZero: true })}
              />
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}

// ── Stepper ──────────────────────────────────────────────────────────────────

export interface Step {
  label: string;
  meta?: string;
  state: 'done' | 'now' | 'pending';
}

/**
 * A row of steps, and one line about the one you are on.
 *
 * This replaces a vertical timeline that gave all six stages of a deal equal
 * weight and two lines each — a third of the screen spent telling you five
 * things you already know so it could tell you the sixth. Horizontally the whole
 * pipeline is one band, and the only prose is the caption under the step that is
 * actually current, which is the one piece of it anybody reads.
 *
 * The same component draws the two-step new-deal wizard, so "where am I" looks
 * the same whether you are entering a deal or working one.
 */
function Stepper({ steps, onStepClick }: { steps: Step[]; onStepClick?: (index: number) => void }) {
  const current = steps.find((s) => s.state === 'now') ?? steps[steps.length - 1];

  return (
    <div className="flex flex-col gap-2">
      <ol className="flex list-none items-center gap-0 overflow-x-auto p-0 [scrollbar-width:none]">
        {steps.map((step, i) => {
          const done = step.state === 'done';
          const now = step.state === 'now';
          const body = (
            <>
              <span
                aria-hidden="true"
                className={cn(
                  'grid size-[18px] shrink-0 place-items-center rounded-full text-[10px] font-semibold',
                  done && 'bg-[var(--credit)] text-[var(--accent-ink)]',
                  now && 'bg-[var(--warn)] text-[#1a1405] ring-4 ring-[var(--warn-soft)]',
                  !done && !now && 'bg-[var(--line)] text-[var(--ink-3)]',
                )}
              >
                {done ? '✓' : i + 1}
              </span>
              <span
                className={cn(
                  'text-[12px] whitespace-nowrap',
                  now ? 'font-semibold text-[var(--ink)]' : 'text-[var(--ink-3)]',
                )}
              >
                {step.label}
              </span>
            </>
          );

          return (
            <li key={step.label} className="flex min-w-0 items-center gap-2">
              {i > 0 && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'h-px w-5 shrink-0 sm:w-8',
                    done || now ? 'bg-[var(--credit)]/50' : 'bg-[var(--line)]',
                  )}
                />
              )}
              {onStepClick ? (
                <button
                  type="button"
                  onClick={() => onStepClick(i)}
                  aria-current={now ? 'step' : undefined}
                  className="flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0"
                >
                  {body}
                </button>
              ) : (
                <span aria-current={now ? 'step' : undefined} className="flex items-center gap-2">
                  {body}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {current?.meta && (
        <p className="m-0 text-[11.5px] text-[var(--ink-3)]">
          <span className="text-[var(--ink-2)]">{current.label}</span> · {current.meta}
        </p>
      )}
    </div>
  );
}

// ── Action panels ────────────────────────────────────────────────────────────

function PanelShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-t border-[var(--line-2)] bg-[var(--surface-2)] p-[var(--pad)]">
      {children}
    </div>
  );
}

/**
 * Cancelling a deal, and saying why.
 *
 * A deal is cancelled rather than deleted once it has left draft, so the
 * register keeps the record of what was agreed — and a row that says "this was
 * agreed and then was not" is unreadable without the reason beside it. The
 * button used to send the literal string "Cancelled from deal entry", which
 * records which control was pressed rather than what happened.
 *
 * The reason is required here and required again in `brokerage.ts`: this is the
 * courtesy, that is the rule. Cancelling is also the one transition that cannot
 * be walked back — `advanceDeal` refuses to move a cancelled deal at all — so
 * the panel says so before the button is pressed rather than after.
 *
 * The panel used to carry a second warning, about a commission invoice this does
 * not withdraw. It is gone because the case is gone: an invoiced deal no longer
 * offers the button at all.
 */
function CancelPanel({
  header,
  busy,
  onClose,
  run,
}: {
  header: DealHeader;
  busy: boolean;
  onClose: () => void;
  run: <T extends { message: string }>(work: () => Promise<T>) => Promise<T | null>;
}) {
  const [reason, setReason] = useState('');
  const [when, setWhen] = useState(today());

  return (
    <PanelShell>
      <p className="bb-eyebrow m-0 pb-3">Cancel {header.dealNo}</p>

      <Callout tone="warn" title="A cancelled deal stays in the register, and cannot be reopened">
        Nothing is deleted: the deal, its goods lines and any settlements recorded against it stay
        exactly as they are, marked cancelled. What this changes is that it can never move again —
        not to delivered, not to closed — and no commission can be invoiced on it.
      </Callout>

      <div className="grid grid-cols-1 gap-2.5 pt-3 md:grid-cols-[160px_minmax(0,1fr)]">
        <Field label="Cancelled on" required>
          <Input size="sm" type="date" value={when} onChange={(e) => setWhen(e.target.value)} />
        </Field>
        <Field label="Reason" required hint="Written to the deal and shown beside it from then on">
          <Textarea
            rows={2}
            value={reason}
            placeholder="Buyer withdrew — supplier could not ship before the LC expiry"
            onChange={(e) => setReason(e.target.value)}
            aria-label="Reason for cancelling"
          />
        </Field>
      </div>

      <Toolbar>
        <div className="flex-1" />
        <Button size="sm" onClick={onClose} disabled={busy}>
          Keep the deal
        </Button>
        <Button
          size="sm"
          variant="danger"
          loading={busy}
          disabled={busy || reason.trim() === ''}
          title={reason.trim() === '' ? 'A reason is required' : undefined}
          onClick={() =>
            void run(() =>
              brokerage.advanceDeal(header.dealNo, 'CANCELLED', {
                date: when,
                reason: reason.trim(),
              }),
            ).then(onClose)
          }
        >
          Cancel deal
        </Button>
      </Toolbar>
    </PanelShell>
  );
}

function SettlementPanel({
  header,
  refs,
  busy,
  onClose,
  run,
}: {
  header: DealHeader;
  refs: BrokerageRefs;
  busy: boolean;
  onClose: () => void;
  run: <T extends { message: string }>(work: () => Promise<T>) => Promise<T | null>;
}) {
  const [form, setForm] = useState({
    settlementDate: today(),
    currency: baseCurrency(),
    fxRate: '1',
    amount: header.balanceBase,
    paymentMethod: 'BANK_TRANSFER',
    proofReference: '',
    confirmed: true,
  });

  return (
    <PanelShell>
      <p className="bb-eyebrow m-0 pb-3">Party B paid Party A</p>
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
        <Field label="Date" required>
          <Input
            size="sm"
            type="date"
            value={form.settlementDate}
            onChange={(e) => setForm({ ...form, settlementDate: e.target.value })}
          />
        </Field>
        <CurrencyRateFields
          currency={form.currency}
          fxRate={form.fxRate}
          onChange={(next) => setForm({ ...form, ...next })}
        />
        <Field label="Amount" required>
          <Input
            size="sm"
            numeric
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
          />
        </Field>
        <Field label="Method">
          <Select
            size="sm"
            value={form.paymentMethod}
            onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
          >
            {refs.enums.paymentMethods.map((m) => (
              <option key={m} value={m}>
                {m.toLowerCase().replace(/_/g, ' ')}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Proof reference" className="md:col-span-2">
          <Input
            size="sm"
            value={form.proofReference}
            onChange={(e) => setForm({ ...form, proofReference: e.target.value })}
            placeholder="SWIFT ref, deposit slip, LC no."
          />
        </Field>
        <div className="flex items-end pb-1.5">
          <Checkbox
            checked={form.confirmed}
            onChange={(e) => setForm({ ...form, confirmed: e.target.checked })}
            label="Confirmed by Party A"
          />
        </div>
      </div>
      <p className="m-0 pt-2 pb-3 text-[11.5px] text-[var(--ink-3)]">
        An unconfirmed tranche is a claim, not a payment — it is recorded but releases no
        commission. Nothing here posts to the general ledger.
      </p>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          loading={busy}
          disabled={!isDecimal(form.amount) || Number(form.amount) <= 0}
          onClick={async () => {
            const ok = await run(() =>
              brokerage.recordSettlement(header.dealNo, {
                ...form,
                proofReference: form.proofReference || null,
              }),
            );
            if (ok) onClose();
          }}
        >
          Record settlement
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </PanelShell>
  );
}

function InvoicePanel({
  header,
  splits,
  invoiceable,
  busy,
  onClose,
  run,
}: {
  header: DealHeader;
  /** Read only for the sub-agent shares, which post on this voucher. */
  splits: DealSplit[];
  invoiceable: string;
  busy: boolean;
  onClose: () => void;
  run: <T extends { message: string }>(work: () => Promise<T>) => Promise<T | null>;
}) {
  const [form, setForm] = useState({ invoiceDate: today(), dueDate: '', whtPct: '0' });

  /**
   * What a sub-agent takes out of this invoice.
   *
   * Their share posts on **this** voucher — Dr commission expense, Cr the
   * agent's own payable — so the panel names it before the button is pressed.
   * A salesperson's share is deliberately absent: that one is an accrual the
   * month-end run posts, and listing the two together would say that pressing
   * this button pays both.
   *
   * Computed from the shares rather than read back, because the figure does not
   * exist until the voucher does. It is stated as an estimate for the same
   * reason: `accrueSubAgentCommission` rounds each share with `round_money` in
   * PostgreSQL, and a browser that disagreed by a paisa would look like a bug in
   * the posting rather than in the preview.
   */
  const subAgentShare = roundDecimalString(
    sumDecimal(
      splits
        .filter((s) => s.kind === 'Sub-agent')
        .map((s) =>
          s.fixedAmountBase
            ? s.fixedAmountBase
            : s.sharePct
              ? mulDecimal(mulDecimal(invoiceable, s.sharePct), '0.01')
              : '0',
        ),
    ),
    4,
  );
  const shared = cmpDecimal(subAgentShare, '0') > 0;

  return (
    <PanelShell>
      <p className="bb-eyebrow m-0 pb-3">Commission invoice on {header.partyA}</p>
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
        <Field label="Invoice date" required>
          <Input
            size="sm"
            type="date"
            value={form.invoiceDate}
            onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })}
          />
        </Field>
        <Field label="Due date">
          <Input
            size="sm"
            type="date"
            value={form.dueDate}
            onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
          />
        </Field>
        <Field label="Withholding %" hint="Deducted by Party A on payment">
          <Input
            size="sm"
            numeric
            value={form.whtPct}
            onChange={(e) => setForm({ ...form, whtPct: e.target.value })}
          />
        </Field>
        <Field label="Amount">
          <Input size="sm" numeric readOnly value={formatMoney(invoiceable)} />
        </Field>
      </div>
      <Callout tone="warn" title="This one posts">
        Dr Commission Receivable · Cr Commission Income for{' '}
        {formatMoney(invoiceable, { showCurrency: true })}, as a <strong>COM</strong> voucher into
        the open period.
        {shared && (
          <>
            {' '}
            The same voucher books the sub-agent shares — Dr Commission — Sub-agents · Cr each
            agent’s own payable, about {formatMoney(subAgentShare, { showCurrency: true })} in all,
            computed exactly in the database. A salesperson’s share is not on it: that accrues here
            and posts in the month-end commission run.
          </>
        )}{' '}
        The goods and Party B’s payments to Party A stay memorandum.
      </Callout>
      <div className="flex gap-2 pt-3">
        <Button
          size="sm"
          variant="primary"
          loading={busy}
          onClick={async () => {
            const ok = await run(() =>
              brokerage.raiseCommissionInvoice(header.dealNo, {
                invoiceDate: form.invoiceDate,
                dueDate: form.dueDate || null,
                whtPct: form.whtPct || '0',
              }),
            );
            if (ok) onClose();
          }}
        >
          Post commission invoice
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </PanelShell>
  );
}

/**
 * The commission arrives, and it arrives at a different rate than it was billed
 * at.
 *
 * The invoice was raised at the rate on the deal, which on the day it was struck
 * was an **estimate**. What Party A actually remits converts at whatever the
 * rate is when the money lands. Both are facts and the book keeps both: the
 * receivable clears at what it was carried at, the bank takes what the money was
 * worth, and the gap posts to realised FX gain or loss. Leaving the field alone
 * receives at the estimate, which is exactly what this screen used to do — and
 * silently, which is what made it wrong.
 *
 * The rate field renders only for a commission billed in a foreign currency. On
 * a book billing in its own currency there is no second rate to have, and the
 * server refuses one rather than ignoring it.
 */
function ReceiptPanel({
  invoiceNo,
  currency,
  estimatedRate,
  busy,
  onClose,
  run,
}: {
  invoiceNo: string;
  /** The currency the commission was billed in — from the deal. */
  currency: string;
  /** What the invoice converted at. The default, and what the gain is measured
   *  against. */
  estimatedRate: string;
  busy: boolean;
  onClose: () => void;
  run: <T extends { message: string }>(work: () => Promise<T>) => Promise<T | null>;
}) {
  const banks = useResource<{ accounts: Array<{ code: string; name: string; isBank: boolean }> }>(
    '/accounts',
  );
  const foreign = currency !== baseCurrency();
  const [form, setForm] = useState({
    receiptDate: today(),
    bankAccountCode: '',
    fxRate: estimatedRate,
  });
  const options = (banks.data?.accounts ?? []).filter((a) => a.isBank);
  const rateMoved =
    foreign && isDecimal(form.fxRate) && cmpDecimal(form.fxRate, estimatedRate) !== 0;

  return (
    <PanelShell>
      <p className="bb-eyebrow m-0 pb-3">Commission received against {invoiceNo}</p>
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
        <Field label="Receipt date" required>
          <Input
            size="sm"
            type="date"
            value={form.receiptDate}
            onChange={(e) => setForm({ ...form, receiptDate: e.target.value })}
          />
        </Field>
        <Field label="Bank" className="md:col-span-2">
          <Select
            size="sm"
            value={form.bankAccountCode}
            onChange={(e) => setForm({ ...form, bankAccountCode: e.target.value })}
          >
            <option value="">— main {baseCurrency()} account —</option>
            {options.map((a) => (
              <option key={a.code} value={a.code}>
                {a.code} · {a.name}
              </option>
            ))}
          </Select>
        </Field>
        {foreign && (
          <Field
            label={`Actual rate ${currency} → ${baseCurrency()}`}
            required
            hint={`Invoiced at ${estimatedRate}`}
          >
            <Input
              size="sm"
              numeric
              value={form.fxRate}
              invalid={!isDecimal(form.fxRate) || Number(form.fxRate) <= 0}
              onChange={(e) => setForm({ ...form, fxRate: e.target.value })}
            />
          </Field>
        )}
      </div>
      <p className="m-0 pt-2 pb-3 text-[11.5px] text-[var(--ink-3)]">
        Posts a BR voucher: the net to the bank, the withholding Party A deducted to withholding tax
        receivable, and the receivable cleared in full. A receipt of only the net would leave a
        permanent stub on the party ledger.
      </p>
      {rateMoved && (
        <Callout tone="info" title="This posts an exchange difference">
          The receivable clears at {estimatedRate}, the rate it was invoiced at — that is what
          posted, and it is not revised after the event. The bank takes what the money was worth at{' '}
          {Number(form.fxRate)}. The difference is realised exchange{' '}
          {cmpDecimal(form.fxRate, estimatedRate) > 0 ? 'gain' : 'loss'} and takes its own line. The
          exact figure is computed in the database and named in the confirmation.
        </Callout>
      )}
      <div className="flex gap-2 pt-3">
        <Button
          size="sm"
          variant="primary"
          loading={busy}
          disabled={foreign && (!isDecimal(form.fxRate) || Number(form.fxRate) <= 0)}
          onClick={async () => {
            const ok = await run(() =>
              brokerage.recordCommissionReceipt(invoiceNo, {
                receiptDate: form.receiptDate,
                bankAccountCode: form.bankAccountCode || null,
                ...(foreign ? { fxRate: form.fxRate } : {}),
              }),
            );
            if (ok) onClose();
          }}
        >
          Post receipt
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </PanelShell>
  );
}

// ── Form ─────────────────────────────────────────────────────────────────────

/**
 * The two steps a new deal is entered in.
 *
 * The form used to be one screen of forty-odd fields, most of which nobody knows
 * on the day a deal is struck — the vessel is not booked, the B/L does not
 * exist, and a contract number is a thing that arrives by email next week.
 * Splitting it in two puts the questions that *are* answerable first and the
 * money second, and it means the first screen fits without scrolling.
 */
const DEAL_STEPS = [
  { key: 'deal', title: 'Deal & goods', hint: 'Who is trading, and what' },
  { key: 'commission', title: 'Commission', hint: 'What we earn, and when' },
] as const;

/** Which complaints belong to which step, so Next only blocks on its own step. */
function stepIssues(draft: DealDraft, step: number): string[] {
  const all = complaints(draft);
  const commissionWords = ['commission', 'flat commission'];
  const isCommission = (issue: string) =>
    commissionWords.some((w) => issue.toLowerCase().includes(w));
  return step === 0 ? all.filter((i) => !isCommission(i)) : all.filter(isCommission);
}

/**
 * The goods picker, and the one escape hatch it needs.
 *
 * Brokered goods are often things we never stock — `product_availability` says
 * so in its own comment, and `deal_lines` stores a **name**, not a product id —
 * so the cell cannot simply be a dropdown over the catalogue. It also cannot
 * stay what it was: a text box with a `<datalist>` behind it, which the browser
 * matches from the first character of the label, so an operator who knows the
 * item as "soybean meal" could not find `Argentine Soybean Meal 46%` by typing
 * it, and nothing on screen said what Party A had actually offered.
 *
 * So: a `Combobox` by default, filtering word-wise across every offer Party A
 * has on the availability map — and only those, because our own catalogue is
 * what we stock and says nothing about what this supplier can supply — with a
 * toggle to a plain box for the genuinely new name. The toggle starts on the box
 * when the
 * line already holds a name nothing in the list matches, because that is what a
 * saved free-text line looks like when it is re-opened for editing.
 */
function ProductCell({
  value,
  options,
  onPick,
  onType,
}: {
  value: string;
  options: readonly { value: string; label: string; hint?: string; group?: string }[];
  onPick: (name: string) => void;
  onType: (name: string) => void;
}) {
  const [freeText, setFreeText] = useState(
    () => value !== '' && !options.some((o) => o.value === value),
  );

  return (
    <div className="flex items-center gap-1">
      {freeText ? (
        <Input
          size="sm"
          value={value}
          onChange={(e) => onType(e.target.value)}
          placeholder="Soybean meal 46%"
        />
      ) : (
        <Combobox
          variant="field"
          aria-label="Product"
          placeholder="Search what Party A has offered…"
          emptyText="Nothing on Party A’s availability map matches — use ✎ to type a name"
          value={value}
          options={options}
          onChange={onPick}
        />
      )}
      <Button
        size="sm"
        variant="ghost"
        aria-label={freeText ? 'Pick from the list instead' : 'Type a name we do not carry'}
        title={freeText ? 'Pick from the list instead' : 'Type a name we do not carry'}
        onClick={() => setFreeText(!freeText)}
      >
        {freeText ? '⌄' : '✎'}
      </Button>
    </div>
  );
}

function DealForm({
  draft,
  refs,
  busy,
  notice,
  problem,
  onChange,
  onCancel,
  onSave,
}: {
  draft: DealDraft;
  refs: BrokerageRefs;
  busy: boolean;
  notice: string | null;
  problem: string | null;
  onChange: (draft: DealDraft) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const [step, setStep] = useState(0);

  const set = <K extends keyof DealDraft>(key: K, value: DealDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const setLine = (index: number, patch: Partial<LineDraft>) =>
    onChange({
      ...draft,
      lines: draft.lines.map((l, i) => (i === index ? { ...l, ...patch } : l)),
    });

  // The two grids grow their own last row, so neither an extra goods line nor a
  // commission split costs a click to reach.
  useAutoRows(
    draft.lines,
    (lines) => onChange({ ...draft, lines }),
    () => ({ ...EMPTY_LINE }),
    (l) => l.productName === '' && l.quantity === '',
  );
  useAutoRows(
    draft.splits,
    (splits) => onChange({ ...draft, splits }),
    () => ({ ...EMPTY_SPLIT }),
    (s) => s.beneficiary === '',
  );

  const totals = preview(draft);
  const issues = stepIssues(draft, step);
  const allIssues = complaints(draft);

  /**
   * The two pickers read the brokerage register, and nothing else.
   *
   * They used to fall back to `isSupplier` and `isCustomer` — every trade
   * counterparty in the book — which offered a feed supplier we buy from as a
   * Party A who owes us commission, and a distribution customer as a Party B who
   * pays somebody else. Those are different relationships: a Party A holds a
   * commission receivable under `1-1-3-2` since `0040` and a supplier holds a
   * payable under `2-1-1`, and a party picked from the wrong list has the wrong
   * one. Both lists are maintained in **Brokerage → Deal Parties**.
   */
  const sellers = refs.parties.filter((p) => p.isSeller);
  const buyers = refs.parties.filter((p) => p.isBuyer);
  const subAgents = refs.parties.filter((p) => p.isSubAgent);
  const last = step === DEAL_STEPS.length - 1;

  /**
   * A deal starts from the availability map, so the goods picker does too.
   *
   * The whole map is fetched once and narrowed to Party A here rather than
   * asked for per supplier: it is intel about a few dozen products, the
   * register screen already loads all of it, and re-requesting on every change
   * of the Party A picker would make the goods list flicker while somebody is
   * still deciding who they are buying from.
   */
  const availability = useResource<{ rows: AvailabilityRow[] }>('/product-availability');
  const partyAName = sellers.find((p) => p.code === draft.partyACode)?.name ?? 'Party A';
  const offers = useMemo(
    () =>
      draft.partyACode
        ? (availability.data?.rows ?? []).filter((r) => r.supplierCode === draft.partyACode)
        : [],
    [availability.data, draft.partyACode],
  );
  const offerByName = useMemo(() => new Map(offers.map((o) => [o.product, o])), [offers]);

  /**
   * The list is Party A's own offers, and nothing else.
   *
   * It used to carry the whole catalogue underneath them, which is the wrong
   * question on this screen twice over: the catalogue is what **we** stock, and
   * a brokered deal is goods we never own — so an item we hold in a warehouse is
   * no evidence at all that this supplier can supply it. Offering four hundred
   * SKUs under a heading that says "Catalogue" invited a line naming a product
   * Party A has never quoted.
   *
   * The escape hatch is unchanged and is what makes the narrowing safe:
   * `deal_lines` stores a **name**, so `✎` still takes any name at all, which is
   * how a genuinely new item is brokered before it reaches the availability map.
   */
  const productOptions = useMemo(
    () =>
      offers.map((o) => ({
        value: o.product,
        label: o.product,
        hint: [
          o.grade,
          o.rate ? `${o.currency} ${o.rate}${o.rateUomCode ? ` / ${o.rateUomCode}` : ''}` : null,
          humanizeStatus(o.status),
        ]
          .filter(Boolean)
          .join(' · '),
        group: `Available from ${partyAName}`,
      })),
    [offers, partyAName],
  );

  /**
   * What a picked offer fills in, and what it leaves alone.
   *
   * The unit follows the pick because it is a property of the item — the same
   * rule `pickedLine()` applies on every other document in the application. The
   * rate does not: it is what two parties agreed, the map holds only an
   * *indicative* figure, and re-picking the item on a line somebody has
   * negotiated must not quietly move the money. It fills a blank rate only, and
   * only when the offer is quoted in the deal's own currency — an indicative
   * USD rate dropped into a PKR deal is a number that looks entered.
   */
  /**
   * Who a split row may still name.
   *
   * Every other row's choice is taken out, so nobody can be given two shares of
   * one commission. That is not a tidiness rule: `deal_commission_splits` is
   * summed and checked against the gross commission, and one beneficiary on two
   * rows is either a duplicate that overstates what is owed to them or an
   * arrangement that should have been one line — and the ledger, which opens one
   * payable account per person, cannot tell the two apart afterwards.
   *
   * The row's **own** current value stays in its list, or the control would be
   * showing a selection it no longer offers.
   */
  const beneficiaryOptions = (row: number) => {
    const taken = new Set(
      draft.splits.filter((s, i) => i !== row && s.beneficiary !== '').map((s) => s.beneficiary),
    );
    const free = (value: string) => value === draft.splits[row]?.beneficiary || !taken.has(value);
    return [
      ...subAgents
        .map((p) => ({
          value: `party:${p.code}`,
          label: p.name,
          hint: p.code,
          group: 'Sub-agents',
        }))
        .filter((o) => free(o.value)),
      ...refs.salespersons
        .map((s) => ({
          value: `sales:${s.code}`,
          label: s.name,
          hint: s.code,
          group: 'Salespersons',
        }))
        .filter((o) => free(o.value)),
    ];
  };

  const pickProduct = (index: number, name: string) => {
    const line = draft.lines[index];
    if (!line) return;
    const offer = offerByName.get(name);
    const patch: Partial<LineDraft> = { productName: name };
    if (line.quantity.trim() === '') patch.quantity = '1';
    if (offer?.rateUomCode && refs.uoms.some((u) => u.code === offer.rateUomCode)) {
      patch.uomCode = offer.rateUomCode;
    }
    if (offer?.rate && offer.currency === draft.currency && !isDecimal(line.unitRate)) {
      patch.unitRate = offer.rate;
    }
    setLine(index, patch);
  };

  return (
    <>
      <PageHeader
        breadcrumb="Brokerage / Deal Entry"
        title={draft.dealNo ? `Edit ${draft.dealNo}` : 'New deal'}
        description={
          draft.dealNo
            ? 'A draft can still be changed until the deal is confirmed.'
            : `Enter a new deal in two steps; it will be numbered ${refs.nextDealNo ?? 'when you save'}.`
        }
        actions={
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        }
      />

      <div className="pb-3.5">
        <Stepper
          steps={DEAL_STEPS.map((s, i) => ({
            label: s.title,
            meta: s.hint,
            state: i < step ? 'done' : i === step ? 'now' : 'pending',
          }))}
          onStepClick={(i) => {
            // Backwards is always allowed; forwards only once this step is clean.
            if (i < step || issues.length === 0) setStep(i);
          }}
        />
      </div>

      <div className="flex flex-col gap-2.5 pb-3.5">
        {problem && (
          <Callout tone="danger" title="This deal was not saved">
            {problem}
          </Callout>
        )}
        {notice && (
          <Callout tone="success" title="Saved">
            {notice}
          </Callout>
        )}
        {issues.length > 0 && (
          <Callout tone="warn" title="Before this step is complete">
            <ul className="m-0 list-disc pl-4 text-[12.5px]">
              {issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </Callout>
        )}
      </div>

      {step === 0 ? (
        <div className="flex flex-col gap-3.5">
          <Card flush>
            <CardHeader title="The deal" subtitle="Who is trading, when, and in what currency" />
            <CardBody className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
              <Field label="Deal date" required>
                <Input
                  size="sm"
                  type="date"
                  value={draft.dealDate}
                  onChange={(e) => set('dealDate', e.target.value)}
                />
              </Field>
              <Field label="Kind" required>
                <Select
                  size="sm"
                  value={draft.kind}
                  onChange={(e) => set('kind', e.target.value as 'LOCAL' | 'IMPORT')}
                >
                  <option value="LOCAL">Local</option>
                  <option value="IMPORT">Import</option>
                </Select>
              </Field>
              {/* Picking the currency fills in its live rate — see
                  `CurrencyRateFields`. This pair used to offer USD and PKR only
                  and leave the old rate under a changed code. */}
              <CurrencyRateFields
                currency={draft.currency}
                fxRate={draft.fxRate}
                label="Deal currency"
                rateLabel={`Rate to ${baseCurrency()}`}
                onChange={(next) => onChange({ ...draft, ...next })}
              />

              <Field label="Party A — supplier, pays us" required className="md:col-span-2">
                <Combobox
                  variant="field"
                  aria-label="Party A"
                  placeholder="Type a supplier name or code…"
                  value={draft.partyACode}
                  options={sellers.map((p) => ({
                    value: p.code,
                    label: p.name,
                    hint: p.code,
                  }))}
                  onChange={(v) => set('partyACode', v)}
                />
              </Field>
              <Field label="Party B — buyer, pays Party A" required className="md:col-span-2">
                <Combobox
                  variant="field"
                  aria-label="Party B"
                  placeholder="Type a buyer name or code…"
                  value={draft.partyBCode}
                  options={buyers.map((p) => ({
                    value: p.code,
                    label: p.name,
                    hint: p.code,
                  }))}
                  onChange={(v) => set('partyBCode', v)}
                />
              </Field>
            </CardBody>
          </Card>

          <Card flush>
            <CardHeader title="Goods" subtitle="Memorandum — quantity × rate, never posted" />
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[var(--surface-2)]">
                    <Th>Product</Th>
                    <Th align="right" width="130px">
                      Quantity
                    </Th>
                    <Th width="100px">Unit</Th>
                    <Th align="right" width="150px">
                      Rate
                    </Th>
                    <Th align="right" width="170px">
                      Value
                    </Th>
                    <Th width="52px"> </Th>
                  </tr>
                </thead>
                <tbody>
                  {draft.lines.map((line, i) => (
                    <tr key={i} className="border-b border-[var(--line-2)]">
                      <td className="px-2 py-1.5">
                        <ProductCell
                          key={`${draft.partyACode}-${i}`}
                          value={line.productName}
                          options={productOptions}
                          onPick={(name) => pickProduct(i, name)}
                          onType={(name) => setLine(i, { productName: name })}
                        />
                      </td>
                      <td className="px-1 py-1.5">
                        <Input
                          size="sm"
                          numeric
                          value={line.quantity}
                          onChange={(e) => setLine(i, { quantity: e.target.value })}
                        />
                      </td>
                      <td className="px-1 py-1.5">
                        <Select
                          size="sm"
                          value={line.uomCode}
                          onChange={(e) => setLine(i, { uomCode: e.target.value })}
                        >
                          <option value="">—</option>
                          {refs.uoms.map((u) => (
                            <option key={u.code} value={u.code}>
                              {u.code}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td className="px-1 py-1.5">
                        <Input
                          size="sm"
                          numeric
                          value={line.unitRate}
                          onChange={(e) => setLine(i, { unitRate: e.target.value })}
                        />
                      </td>
                      <td className="bb-num px-2 py-1.5 text-right text-[12.5px] tabular-nums">
                        {isDecimal(line.quantity) && isDecimal(line.unitRate)
                          ? formatMoney(
                              roundDecimalString(mulDecimal(line.quantity, line.unitRate), 4),
                              { decimals: 0 },
                            )
                          : '—'}
                      </td>
                      <td className="px-1 py-1.5 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Remove line ${i + 1}`}
                          disabled={draft.lines.length === 1}
                          onClick={() =>
                            onChange({
                              ...draft,
                              lines: draft.lines.filter((_, index) => index !== i),
                            })
                          }
                        >
                          ×
                        </Button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bb-row-h bg-[var(--surface-2)]">
                    <td className="px-2 text-[12.5px] font-semibold" colSpan={4}>
                      Deal value · {draft.currency}
                    </td>
                    <td className="bb-num px-2 text-right text-[13px] font-semibold tabular-nums">
                      {formatMoney(totals.value, { decimals: 0 })}
                    </td>
                    <td />
                  </tr>
                  <tr className="bb-row-h">
                    <td className="px-2 text-[12px] text-[var(--ink-3)]" colSpan={4}>
                      PKR equivalent — memorandum only, no GL entry
                    </td>
                    <td className="bb-num px-2 text-right text-[12.5px] tabular-nums">
                      {formatMoney(totals.valueBase, { decimals: 0 })}
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          {/*
            What Party A said they could supply, beside the lines being typed.

            This is the same `product_availability` the register draws, and it
            is here because it is where a brokered deal actually starts: the
            picker above offers these rows first, and seeing the indicative rate
            and how stale it is beside the rate being entered is the difference
            between a suggestion and a number somebody trusts. It renders only
            once Party A is chosen — before that there is nothing to be
            available.
          */}
          {draft.partyACode && (
            <Card flush>
              <CardHeader
                title="Product availability"
                subtitle={`What ${partyAName} has told us they can supply — indicative, not a quote`}
              />
              {offers.length === 0 ? (
                <CardBody>
                  <p className="m-0 text-[12.5px] text-[var(--ink-3)]">
                    Nothing is on the availability map for {partyAName}. The goods lines above still
                    take any name — the map is intel, not a catalogue.
                  </p>
                </CardBody>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[var(--surface-2)]">
                        <Th>Product</Th>
                        <Th width="150px">Origin / grade</Th>
                        <Th align="right" width="170px">
                          Indicative rate
                        </Th>
                        <Th width="120px">Status</Th>
                        <Th width="140px">Last confirmed</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {offers.map((o) => (
                        <tr key={o.id} className="bb-row-h border-b border-[var(--line-2)]">
                          <td className="px-[var(--pad)] text-[13px] font-medium">{o.product}</td>
                          <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">
                            {[o.origin, o.grade].filter(Boolean).join(' · ') || '—'}
                          </td>
                          <td className="bb-num px-2.5 text-right text-[12.5px] tabular-nums">
                            {o.rate
                              ? `${o.currency} ${formatMoney(o.rate)}${o.rateUomCode ? ` / ${o.rateUomCode}` : ''}`
                              : '—'}
                          </td>
                          <td className="px-2.5 text-[12.5px]">
                            <Badge status={o.status}>{humanizeStatus(o.status)}</Badge>
                          </td>
                          <td className="px-[var(--pad)] text-[12.5px] text-[var(--ink-3)]">
                            {o.lastConfirmed ? formatDate(o.lastConfirmed) : 'never'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </div>
      ) : (
        <div className="grid gap-3.5 xl:grid-cols-[1.4fr_1fr]">
          <div className="flex flex-col gap-3.5">
            <Card flush>
              <CardHeader
                title="Commission"
                subtitle="What we earn, and when it becomes ours to invoice"
              />
              <CardBody className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
                <Field label="Basis" required className="md:col-span-2">
                  <Select
                    size="sm"
                    value={draft.commissionBasis}
                    onChange={(e) => set('commissionBasis', e.target.value)}
                  >
                    <option value="PCT_OF_DEAL_VALUE">Percentage of deal value</option>
                    <option value="PER_UNIT">Per unit</option>
                    <option value="FLAT_AMOUNT">Flat amount</option>
                  </Select>
                </Field>

                {draft.commissionBasis === 'FLAT_AMOUNT' ? (
                  <Field label="Amount" required>
                    <Input
                      size="sm"
                      numeric
                      value={draft.commissionAmount}
                      onChange={(e) => set('commissionAmount', e.target.value)}
                    />
                  </Field>
                ) : (
                  <Field
                    label={
                      draft.commissionBasis === 'PCT_OF_DEAL_VALUE' ? 'Percentage' : 'Per unit'
                    }
                    required
                  >
                    <Input
                      size="sm"
                      numeric
                      value={draft.commissionRate}
                      onChange={(e) => set('commissionRate', e.target.value)}
                      placeholder={draft.commissionBasis === 'PCT_OF_DEAL_VALUE' ? '0.85' : '250'}
                    />
                  </Field>
                )}

                <CurrencyRateFields
                  currency={draft.commissionCurrency}
                  fxRate={draft.commissionFxRate}
                  rateLabel={`Commission rate to ${baseCurrency()}`}
                  onChange={(next) =>
                    onChange({
                      ...draft,
                      commissionCurrency: next.currency,
                      commissionFxRate: next.fxRate,
                    })
                  }
                />

                <Field
                  label="Recognition"
                  required
                  className="md:col-span-3"
                  hint="On full settlement waits for the buyer to pay the supplier in full; pro-rata earns in step with each payment."
                >
                  <Select
                    size="sm"
                    value={draft.accrualPolicy}
                    onChange={(e) => set('accrualPolicy', e.target.value)}
                  >
                    <option value="ON_FULL_SETTLEMENT">On full settlement</option>
                    <option value="PRO_RATA">Pro rata</option>
                  </Select>
                </Field>

                <div className="flex justify-between border-t border-[var(--line-2)] pt-2.5 text-[13px] font-semibold md:col-span-4">
                  <span>Commission, PKR</span>
                  <span className="bb-num tabular-nums">{formatMoney(totals.commission)}</span>
                </div>
              </CardBody>
            </Card>

            <Card flush>
              <CardHeader title="Narration" subtitle="What this deal was, in one line" />
              <CardBody>
                <Textarea
                  rows={3}
                  value={draft.narration}
                  onChange={(e) => set('narration', e.target.value)}
                  placeholder="500 MT soybean meal, Rosario to Karachi, brokered for Andes Protein."
                />
              </CardBody>
            </Card>
          </div>

          <Card flush>
            <CardHeader
              title="Commission splits"
              subtitle="Optional — sub-agents and salespeople who share it"
            />
            <CardBody className="flex flex-col gap-2.5">
              {/* One blank row is always present, so naming a beneficiary costs
                  no click at all — the old `Add` button meant a split, which is
                  the common case on a brokered deal, always cost one more than
                  it needed to. */}
              {draft.splits.map((split, i) => (
                <div key={i} className="flex items-end gap-2">
                  <Field label="Beneficiary" className="flex-1">
                    <Combobox
                      variant="field"
                      aria-label={`Split ${i + 1} beneficiary`}
                      placeholder="Type a name…"
                      value={split.beneficiary}
                      options={beneficiaryOptions(i)}
                      onChange={(v) =>
                        onChange({
                          ...draft,
                          splits: draft.splits.map((s, index) =>
                            index === i ? { ...s, beneficiary: v } : s,
                          ),
                        })
                      }
                    />
                  </Field>
                  <Field label="Share %" className="w-[92px]">
                    <Input
                      size="sm"
                      numeric
                      value={split.sharePct}
                      onChange={(e) =>
                        onChange({
                          ...draft,
                          splits: draft.splits.map((s, index) =>
                            index === i
                              ? { ...s, sharePct: e.target.value, fixedAmountBase: '' }
                              : s,
                          ),
                        })
                      }
                    />
                  </Field>
                  {/*
                    The API has always accepted a fixed amount instead of a
                    percentage (`brokerage.ts` validates one or the other) and
                    the form has never offered it — so a flat 50,000 to an agent
                    had to be back-solved into a share of a deal value that had
                    not settled yet. The two are exclusive: filling either clears
                    the other rather than letting both reach the server.
                  */}
                  <Field label="or fixed PKR" className="w-[128px]">
                    <Input
                      size="sm"
                      numeric
                      value={split.fixedAmountBase ?? ''}
                      onChange={(e) =>
                        onChange({
                          ...draft,
                          splits: draft.splits.map((s, index) =>
                            index === i
                              ? { ...s, fixedAmountBase: e.target.value, sharePct: '' }
                              : s,
                          ),
                        })
                      }
                    />
                  </Field>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Remove split ${i + 1}`}
                    onClick={() =>
                      onChange({
                        ...draft,
                        splits: draft.splits.filter((_, index) => index !== i),
                      })
                    }
                  >
                    ×
                  </Button>
                </div>
              ))}
            </CardBody>
          </Card>
        </div>
      )}

      {/* The step controls sit at the bottom, where the eye already is after
          filling the last field, rather than back up in the page header. */}
      <div
        data-noprint="1"
        className="mt-3.5 flex items-center gap-2 rounded-[var(--radius-lg)] border border-[var(--line)] bg-[var(--surface)] px-[var(--pad)] py-3"
      >
        {step > 0 && (
          <Button size="sm" onClick={() => setStep(step - 1)} disabled={busy}>
            Back
          </Button>
        )}
        <span className="text-[12px] text-[var(--ink-3)]">
          Step {step + 1} of {DEAL_STEPS.length} · {DEAL_STEPS[step]?.title}
        </span>
        <div className="flex-1" />
        {last ? (
          <Button
            size="sm"
            variant="primary"
            loading={busy}
            disabled={allIssues.length > 0}
            onClick={onSave}
          >
            {draft.dealNo ? 'Save draft' : 'Create deal'}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="primary"
            disabled={issues.length > 0}
            onClick={() => setStep(step + 1)}
          >
            Next · {DEAL_STEPS[step + 1]?.title}
          </Button>
        )}
      </div>
    </>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div
      className={
        strong
          ? 'flex justify-between border-t border-[var(--line-2)] pt-2.5 text-[13px] font-semibold'
          : 'flex justify-between text-[12.5px]'
      }
    >
      <span className={strong ? undefined : 'text-[var(--ink-2)]'}>{label}</span>
      <span className="bb-num shrink-0 pl-3 tabular-nums">{value}</span>
    </div>
  );
}

function describeBasis(basis: string, rate: string | null): string {
  const trimmed = rate ? Number(rate).toString() : null;
  if (basis === 'PCT_OF_DEAL_VALUE')
    return trimmed ? `${trimmed}% of deal value` : '% of deal value';
  if (basis === 'PER_UNIT') return trimmed ? `${trimmed} per unit` : 'Per unit';
  if (basis === 'FLAT_AMOUNT') return 'Flat amount';
  return humanizeStatus(basis);
}

/**
 * How far the deal has come, and where it is now.
 *
 * **Status decides what is done; the timestamps only supply the date.** A deal
 * can sit at CONFIRMED with no `confirmed_at` — a record imported from a
 * spreadsheet has a state but no history — and reading the timestamps alone
 * would show that deal as still awaiting confirmation while its own status badge
 * says otherwise. The screen must not disagree with itself.
 */
const DEAL_RANK: Record<string, number> = {
  DRAFT: 0,
  CONFIRMED: 1,
  IN_TRANSIT: 2,
  DELIVERED: 3,
  SETTLED: 4,
  INVOICED: 5,
  RECEIVED: 6,
  CLOSED: 7,
  CANCELLED: -1,
};

/** The transitions a person makes. Settled, invoiced and received are facts
 *  about money and are set by the calls that move it. */
const MANUAL_STEPS = [
  { status: 'CONFIRMED', label: 'Confirm deal' },
  { status: 'DELIVERED', label: 'Mark delivered' },
];

function pipeline(
  h: DealHeader,
): Array<{ label: string; meta: string; state: 'done' | 'now' | 'pending' }> {
  const rank = DEAL_RANK[h.status] ?? 0;
  const settled = Number(h.balanceBase) === 0;

  /**
   * One word each, because the stepper puts all six on one line.
   *
   * The sentence that used to be the label — "Commission invoiced on Party A" —
   * is now the caption, and the caption is only rendered for the step you are
   * actually on. Six labels and one line of prose, instead of six of each.
   */
  const steps: Array<{ label: string; meta: string; done: boolean }> = [
    { label: 'Entered', meta: formatDate(h.dealDate), done: true },
    {
      label: 'Confirmed',
      meta: h.confirmedAt ? formatDate(h.confirmedAt) : 'Contract or buyer PO to be attached',
      done: rank >= 1,
    },
    {
      label: 'Delivered',
      meta: h.deliveredAt
        ? `To ${h.partyB} on ${formatDate(h.deliveredAt)}`
        : rank >= 2
          ? 'In transit'
          : 'Not shipped to Party B yet',
      done: rank >= 3,
    },
    {
      label: 'Settled',
      meta: settled
        ? h.settledAt
          ? `Party B paid Party A in full, ${formatDate(h.settledAt)}`
          : 'Party B paid Party A in full'
        : `${formatMoney(h.balanceBase, { decimals: 0 })} still owed to ${h.partyA}`,
      done: settled,
    },
    {
      label: 'Invoiced',
      meta: h.commissionInvoiceNo
        ? `${h.commissionInvoiceNo} · ${formatMoney(h.invoicedBase)} on ${h.partyA}`
        : 'Commission not yet raised on Party A',
      done: Number(h.invoicedBase) > 0,
    },
    {
      label: 'Received',
      meta:
        Number(h.receivedBase) > 0
          ? `${formatMoney(h.receivedBase)} received`
          : 'Awaiting remittance from Party A',
      done: Number(h.receivedBase) > 0,
    },
  ];

  // The first step that is not done is where the deal actually is. Later ones
  // are pending even if an out-of-order field happens to be filled in.
  const current = steps.findIndex((s) => !s.done);
  return steps.map((s, i) => ({
    label: s.label,
    meta: s.meta,
    state: s.done ? 'done' : i === current ? 'now' : 'pending',
  }));
}
